<template>
  <div 
    v-if="isVisible" 
    class="pec-tutor-container"
    :class="{ 'sidebar-not-ready': !isSidebarAvailable && config.mascot.defaultPosition === 'sidebar' }"
    :style="containerStyles"
  >
    <!-- Mascot Display -->
    <Teleport :to="teleportTarget" :disabled="!isSidebarAvailable">
      <div :class="['mascot-wrapper', `position-${config.mascot.defaultPosition}`, { 'in-sidebar': isSidebarAvailable }]">
        <MascotDisplay
          :state="mascotState"
          :config="config"
          :has-available-tutorials="hasAvailableTutorials"
          @click="onMascotClick"
        />
        <MascotMenu 
          v-if="showMascotMenu"
          :has-tutorials="hasAvailableTutorials"
          @open-tutorials="openTutorials"
          @open-feedback="openFeedbackModal"
        />
      </div>
    </Teleport>

    <!-- Feedback Modal -->
    <FeedbackModal
      v-if="showFeedbackModal"
      @close="showFeedbackModal = false"
      @submit="handleFeedbackSubmit"
    />

    <!-- Choice Box -->
    <ChoiceBox
      v-if="currentChoice"
      :title="currentChoice.title"
      :choices="currentChoice.choices"
      @select="currentChoice.onSelect"
    />

    <!-- Message Box -->
    <MessageBox
      v-if="currentMessage"
      :message="currentMessage"
      :config="config"
      @close="closeMessage"
      @response="handleResponse"
    />

    <!-- Tutorial Menu -->
    <TutorialMenu
      v-if="showMenu"
      :tutorials="currentPageTutorials"
      :config="config"
      @start-tutorial="startTutorial"
      @close="showMenu = false"
    />

    <!-- Interaction Overlay -->
    <InteractionOverlay
      v-if="currentInteraction"
      :config="config"
      :interaction-data="currentInteraction"
      @complete="currentInteraction.onComplete"
    />
  </div>

  <!-- Announcement Modal -->
  <div v-if="showAnnouncement" class="announcement-overlay" :style="containerStyles">
    <div class="announcement-modal">
      <div class="announcement-content">
        <h3 class="announcement-title">System Analysis & Update</h3>
        <p class="announcement-text">
          We've deployed new analytical tools and corrections to your workflow.
          You now have access to advanced AI-assisted corrections and new ranking metrics.
        </p>
        <button class="announcement-btn" @click="closeAnnouncement">Acknowledge</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useTutorialManager } from './composables/useTutorialManager';
import type { MediaOptions } from './types';
import { usePageDetection } from './composables/usePageDetection';

import MascotDisplay from './components/MascotDisplay.vue';
import MessageBox from './components/MessageBox.vue';
import TutorialMenu from './components/TutorialMenu.vue';
import InteractionOverlay from './components/InteractionOverlay.vue';
import MascotMenu from './components/MascotMenu.vue';
import FeedbackModal from './components/FeedbackModal.vue';
import ChoiceBox from './components/ChoiceBox.vue';
import { useFeedback } from './composables/useFeedback';

import type { PecTutorConfig, MascotState, MessageOptions, Tutorial, PecTutorAPI, ChoiceOption } from './types';

// Props
interface Props {
  config?: Partial<PecTutorConfig>;
  autoShow?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  autoShow: true
});

const route = useRoute();

// Load default config
const defaultConfig = {
  theme: {
    primaryColor: "#007bff",
    secondaryColor: "#6c757d", 
    backgroundColor: "#ffffff",
    textColor: "#333333",
    borderRadius: "8px"
  },
  timeouts: {
    defaultMessage: 5000,
    tipFadeOut: 3000,
    userInputReminder: 10000,
    attentionShake: 1000
  },
  animation: {
    mascotTransition: 300,
    messageAppear: 200,
    shakeIntensity: 10,
    fadeOutDuration: 500
  },
  behavior: {
    autoHide: true,
    rememberProgress: true,
    showMenuOnLoad: false,
    enableSounds: false
  },
  mascot: {
    defaultPosition: "sidebar",
    size: {
      width: 120,
      height: 120
    }
  }
};

// Reactive state
const isVisible = ref(props.autoShow);
const showMenu = ref(false);
const showMascotMenu = ref(false);
const showFeedbackModal = ref(false);
const currentPath = ref(window.location.pathname);
const currentChoice = ref<{
  title: string;
  choices: ChoiceOption[];
  onSelect?: (choice: ChoiceOption) => void;
} | null>(null);
const currentMessage = ref<{
  text: string;
  type: string;
  duration?: number;
  media?: {
    type: string;
    src: string;
    caption?: string;
    controls?: boolean;
    autoplay?: boolean;
  };
  onClose?: () => void;
  isQuestion?: boolean;
  onResponse?: (response: string) => void;
} | null>(null);
const isInteracting = ref(false);
const currentInteraction = ref<{
  type: string;
  selector?: string;
  from?: string;
  to?: string;
  duration?: number;
  options?: Record<string, unknown>;
  onComplete?: () => void;
} | null>(null);

// Announcement logic
const showAnnouncement = ref(false);

const isSidebarAvailable = ref(false);
const teleportTarget = ref<string | Element>('#pectutor-sidebar-target');
const hasTeleportedSuccessfully = ref(false);

const checkSidebarPresence = () => {
  const sidebarTarget = document.getElementById('pectutor-sidebar-target');
  if (sidebarTarget) {
    isSidebarAvailable.value = true;
    teleportTarget.value = sidebarTarget; // Use actual element for more stability
    hasTeleportedSuccessfully.value = true;
    return true;
  }
  
  const taskbarTarget = document.getElementById('pectutor-taskbar-target');
  if (taskbarTarget) {
    isSidebarAvailable.value = true;
    teleportTarget.value = '#pectutor-taskbar-target';
    return true;
  }
  
  isSidebarAvailable.value = false;
  return false;
};

const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

const CURADORIA_ANNOUNCEMENT_COOKIE = 'curadoria_announcement_read';

const checkAnnouncement = () => {
  if (!route.path.startsWith('/platform/check')) {
    showAnnouncement.value = false;
    return;
  }
  const now = new Date();
  const activePeriodStart = new Date('2024-01-01T00:00:00Z');
  const activePeriodEnd = new Date('2026-12-31T23:59:59Z');

  const isWithinPeriod = now >= activePeriodStart && now <= activePeriodEnd;
  const hasRead =
    getCookie(CURADORIA_ANNOUNCEMENT_COOKIE) === 'true' ||
    getCookie('announcement_read') === 'true';

  if (isWithinPeriod && !hasRead) {
    showAnnouncement.value = true;
  } else {
    showAnnouncement.value = false;
  }
};

const closeAnnouncement = () => {
  showAnnouncement.value = false;
  setCookie(CURADORIA_ANNOUNCEMENT_COOKIE, 'true', 365);
  setCookie('announcement_read', 'true', 365);
};

watch(
  () => route.path,
  () => {
    checkAnnouncement();
  }
);

// Merge configuration
const config = computed(() => ({ ...defaultConfig, ...props.config }) as PecTutorConfig);

// Mascot state
const mascotState = reactive<MascotState>({
  current: 'idle',
  position: { x: 0, y: 0 },
  visible: true
});

// Composables
const { 
  tutorials: availableTutorials, 
  // currentTutorial, // Currently unused
  registerTutorial,
  startTutorial: startTutorialAction,
  stopTutorial
} = useTutorialManager();

const { sendFeedback } = useFeedback();
const { setupPageChangeDetection } = usePageDetection();

/*
const {
  // Visual interaction logic is now handled by InteractionOverlay.vue
} = useInteraction();
*/

// const {
//   getCurrentPage, // Currently unused
//   getTutorialsForPage // Currently unused
// } = usePageDetection();

// Computed styles
const containerStyles = computed(() => ({
  '--primary-color': config.value.theme.primaryColor,
  '--secondary-color': config.value.theme.secondaryColor,
  '--background-color': config.value.theme.backgroundColor,
  '--text-color': config.value.theme.textColor,
  '--border-radius': config.value.theme.borderRadius,
}));

// Check if tutorials are available for current page
const hasAvailableTutorials = computed(() => {
  const currentPageTutorials = availableTutorials.value.filter((tutorial: Tutorial) => 
    tutorial.pageUrl === currentPath.value
  );
  
  return currentPageTutorials.length > 0;
});

// Get tutorials for current page
const currentPageTutorials = computed(() => 
  availableTutorials.value.filter((tutorial: Tutorial) => 
    tutorial.pageUrl === currentPath.value
  )
);

// Methods
const onMascotClick = () => {
  showMascotMenu.value = !showMascotMenu.value;
};

const openTutorials = () => {
  showMascotMenu.value = false;
  showMenu.value = true;
}

const openFeedbackModal = () => {
  showMascotMenu.value = false;
  showFeedbackModal.value = true;
};

const handleFeedbackSubmit = async (feedback: string) => {
  await sendFeedback(feedback);
  showFeedbackModal.value = false;
  // Optionally, show a "thank you" message
  say("Obrigado pelo seu feedback!", { type: 'success', duration: 3000 });
};

const closeMessage = () => {
  // Call the onClose callback if it exists
  if (currentMessage.value?.onClose) {
    currentMessage.value.onClose();
  }
  
  currentMessage.value = null;
  mascotState.current = 'idle';
};

const handleResponse = (response: string) => {
  // Handle user responses to questions
  // Call the onResponse callback if it exists (for questions)
  if (currentMessage.value?.onResponse) {
    currentMessage.value.onResponse(response);
  }
  
  // Clear the message without calling onClose (since onResponse was called)
  currentMessage.value = null;
  mascotState.current = 'idle';
};

const startTutorial = async (tutorialId: string) => {
  showMenu.value = false;
  mascotState.current = 'tutorial';
  await startTutorialAction(tutorialId);
};

const choice = async (title: string, choices: ChoiceOption[]): Promise<void> => {
  return new Promise((resolve) => {
    currentChoice.value = {
      title,
      choices,
      onSelect: (selectedChoice: ChoiceOption) => {
        currentChoice.value = null;
        selectedChoice.action();
        resolve();
      },
    };
  });
};

// API Methods (exposed through template ref)
const say = async (text: string, options: MessageOptions = {}): Promise<void> => {
  return new Promise((resolve) => {
    mascotState.current = 'speaking';
    currentMessage.value = {
      text,
      type: options?.type || 'normal',
      duration: options?.duration || config.value.timeouts.defaultMessage,
      media: options?.media, // Add media support
      onClose: () => {
        resolve();
      }
    };
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ask = async (question: string, _options: MessageOptions = {}): Promise<string> => {
  return new Promise((resolve) => {
    mascotState.current = 'listening';
    currentMessage.value = {
      text: question,
      type: 'question',
      isQuestion: true,
      onResponse: (response: string) => {
        resolve(response);
        closeMessage();
      }
    };
  });
};

const click = async (selector: string, options: Record<string, unknown> = {}): Promise<void> => {
  return new Promise(resolve => {
    isInteracting.value = true;
    currentInteraction.value = { 
      type: 'click', 
      selector, 
      options,
      onComplete: () => {
        isInteracting.value = false;
        currentInteraction.value = null;
        resolve();
      }
    };
  });
};

const clickAndDrag = async (from: string, to: string, options: Record<string, unknown> = {}): Promise<void> => {
  return new Promise(resolve => {
    isInteracting.value = true;
    currentInteraction.value = { 
      type: 'drag', 
      from, 
      to, 
      options,
      onComplete: () => {
        isInteracting.value = false;
        currentInteraction.value = null;
        resolve();
      }
    };
  });
};

const highlight = async (selector: string, duration = 2000): Promise<void> => {
  return new Promise(resolve => {
    isInteracting.value = true;
    currentInteraction.value = {
      type: 'highlight',
      selector,
      duration,
      onComplete: () => {
        isInteracting.value = false;
        currentInteraction.value = null;
        resolve();
      }
    };
  });
};

const showImage = (src: string, options: MediaOptions = {}) => {
  return say(options.caption || 'Image', {
    duration: options.duration,
    type: 'normal',
    media: {
      type: 'image',
      src: src,
      caption: options.caption,
    },
  });
};

const showVideo = (src: string, options: MediaOptions = {}) => {
  return say(options.caption || 'Video', {
    duration: options.duration,
    type: 'normal',
    media: {
      type: 'video',
      src: src,
      caption: options.caption,
      controls: options.controls,
      autoplay: options.autoplay,
    },
  });
};

const waitForInput = async (selector: string, options: Record<string, unknown> = {}): Promise<boolean> => {
  return new Promise((resolve) => {
    const element = document.querySelector(selector);
    if (!element) {
      console.warn(`PecTutor: Element not found for waitForInput: ${selector}`);
      resolve(false);
      return;
    }

    highlight(selector, 500);

    const timeout = options.timeout as number || 30000;
    const reminderInterval = options.reminderInterval as number || 5000;
    const reminderMessage = options.reminderMessage as string || `Please interact with the highlighted element: ${selector}`;

    // eslint-disable-next-line prefer-const
    let timeoutId: number;
    // eslint-disable-next-line prefer-const
    let reminderIntervalId: number;
    let isReminderActive = false;
    let hasInteracted = false;

    const cleanup = () => {
      clearTimeout(timeoutId);
      clearInterval(reminderIntervalId);
      // Remove listeners from element and all its children
      element.removeEventListener('click', onInteraction, true);
      element.removeEventListener('mousedown', onInteraction, true);
      element.removeEventListener('input', onInteraction, true);
      element.removeEventListener('change', onInteraction, true);
      element.removeEventListener('focus', onInteraction, true);
    };

    const onInteraction = () => {
      if (hasInteracted) return;
      hasInteracted = true;
      cleanup();
      resolve(true);
    };

    // Add listeners with capture=true to catch events on child elements too
    element.addEventListener('click', onInteraction, { capture: true, once: false });
    element.addEventListener('mousedown', onInteraction, { capture: true, once: false });
    element.addEventListener('input', onInteraction, { capture: true, once: false });
    element.addEventListener('change', onInteraction, { capture: true, once: false });
    element.addEventListener('focus', onInteraction, { capture: true, once: false });

    timeoutId = window.setTimeout(() => {
      if (hasInteracted) return;
      cleanup();
      if (options.onTimeout) {
        (options.onTimeout as () => void)();
      }
      resolve(false);
    }, timeout);

    reminderIntervalId = window.setInterval(async () => {
      if (!isReminderActive && !hasInteracted) {
        isReminderActive = true;
        await say(reminderMessage, { type: 'attention', duration: 2000 });
        await highlight(selector, 1000);
        isReminderActive = false;
      }
    }, reminderInterval);
  });
};

const show = () => {
  isVisible.value = true;
};

const hide = () => {
  isVisible.value = false;
  showMenu.value = false;
  currentMessage.value = null;
};

// Lifecycle
onMounted(() => {
  checkAnnouncement();
  
  // Check if sidebar target exists
  const checkSidebar = setInterval(() => {
    if (checkSidebarPresence()) {
      clearInterval(checkSidebar);
    }
  }, 1000);

  // Setup listener for page changes
  setupPageChangeDetection((newPage) => {
    currentPath.value = newPage;
    // Re-check sidebar presence on page change
    setTimeout(checkSidebarPresence, 500);
  });

  setTimeout(() => {
    if (!getCookie('pectutor_v2_welcomed')) {
      say("Clique em mim para ver os tutoriais. Todas as novas funcionalidades da v2.1 estão explicadas aqui!", { type: 'attention', duration: 8000 });
      setCookie('pectutor_v2_welcomed', 'true', 365);
    }
  }, 2000);

  // Small delay to ensure all imports are resolved
  setTimeout(() => {
    // Connect this component instance to the global PecTutor API
    const globalAPI = (window as Window & { PecTutor?: PecTutorAPI & { _setComponentInstance?: (instance: unknown) => void } }).PecTutor;
    
    if (globalAPI && globalAPI._setComponentInstance) {
      const componentAPI = {
        say,
        ask,
        choice,
        click,
        clickAndDrag,
        highlight,
        showImage,
        showVideo,
        waitForInput,
        show,
        hide,
        isVisible: () => isVisible.value,
        get isInteracting() { return isInteracting.value; },
        set isInteracting(value) { isInteracting.value = value; },
        get currentInteraction() { return currentInteraction.value; },
        set currentInteraction(value) { currentInteraction.value = value; }
      };
      
      // Connect to global API
      globalAPI._setComponentInstance(componentAPI);
    } else {
      console.warn('PecTutor: Global API not found on window');
    }
  }, 50);
});

onUnmounted(() => {
  // Disconnect from global API
  const globalAPI = (window as Window & { PecTutor?: PecTutorAPI & { _setComponentInstance?: (instance: unknown) => void } }).PecTutor;
  if (globalAPI && globalAPI._setComponentInstance) {
    globalAPI._setComponentInstance(null);
  }
});

// Expose API methods
defineExpose({
  say,
  ask,
  choice,
  click,
  clickAndDrag,
  highlight,
  showImage,
  showVideo,
  waitForInput,
  show,
  hide,
  isVisible: () => isVisible.value,
  registerTutorial,
  startTutorial: startTutorialAction,
  stopTutorial
});
</script>

<style scoped>
.pec-tutor-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 9999;
  font-family: system-ui, -apple-system, sans-serif;
  overflow: visible;
}

.pec-tutor-container * {
  box-sizing: border-box;
}

.mascot-wrapper.in-sidebar {
  position: relative !important;
  top: auto !important;
  right: auto !important;
  bottom: auto !important;
  left: auto !important;
  transform: scale(0.6) !important;
  transform-origin: bottom center !important;
  margin-top: auto;
}

.mascot-wrapper {
  z-index: 10000;
  pointer-events: auto;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.3s ease;
}

.pec-tutor-container.sidebar-not-ready .mascot-wrapper,
.mascot-wrapper:not(.in-sidebar):not(.position-bottom-right):not(.position-taskbar) {
  opacity: 0;
  pointer-events: none;
  transform: scale(0);
}

.mascot-wrapper.position-bottom-right {
  position: fixed;
  bottom: 20px;
  right: 20px;
}

.mascot-wrapper.position-taskbar {
  position: fixed;
  top: 15px;
  right: 80px;
  transform: scale(0.45);
  transform-origin: top right;
  z-index: 10001; /* Ensure it is above the right sidebar */
}

.mascot-wrapper.position-sidebar {
  position: relative;
  transform: scale(0.65);
  transform-origin: bottom center;
  z-index: 10001;
  display: flex;
  justify-content: center;
}

/* CSS Custom Properties for theming */
:root {
  --pec-tutor-primary: var(--primary-color, #007bff);
  --pec-tutor-secondary: var(--secondary-color, #6c757d);
  --pec-tutor-background: var(--background-color, #ffffff);
  --pec-tutor-text: var(--text-color, #333333);
  --pec-tutor-border-radius: var(--border-radius, 8px);
}

/* Announcement Modal Styles */
.announcement-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001;
  pointer-events: auto;
}

.announcement-modal {
  background: var(--pec-tutor-background, #ffffff);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.05);
  overflow: hidden;
  animation: modal-enter 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.announcement-content {
  padding: 32px;
}

.announcement-title {
  margin: 0 0 12px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--pec-tutor-text, #1e293b);
  letter-spacing: -0.01em;
}

.announcement-text {
  margin: 0 0 24px 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--pec-tutor-secondary, #64748b);
}

.announcement-btn {
  width: 100%;
  padding: 12px 20px;
  background: var(--pec-tutor-primary, #0f172a);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}


.announcement-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: 0.95;
}

@keyframes modal-enter {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
